<script setup lang="ts">
import {onMounted, ref, watch, computed} from 'vue';
import {handleApiRequest} from 'src/api/handleApiRequest';
import MultiViewLayout from 'src/components/layouts/MultiViewLayout.vue';
import {BoxWidthEnum, ButtonStdColorEnum} from 'src/constants/enums';
import {getNextActivationRoute} from 'src/services/piqSetupService';
import {getCanaryHeightWeight, updateCanaryHeightWeight} from 'src/api/endpoints/patientWeb';
import {type CanaryPatientHeightWeight} from 'src/types/webContracts';
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {QForm} from 'quasar';
import {useQuasar} from 'quasar';

const $q = useQuasar()
const router = useRouter();
const {t} = useI18n();

const localForm = ref(null);

const props = defineProps({
  contentBoxWidth: {
    type: String,
    default: () => BoxWidthEnum.xs,
  },
});

import TheAbandonModal from '@/components/TheAbandonModal.vue';

const showAbandon = ref(false);

import TheContactModal from '@/components/TheContactModal.vue';
const showContact = ref(false);

const heightFt = [2, 3, 4, 5, 6, 7];
const heightIn = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const heightsOptions: { ft: number, in: number, label: string }[] = (() => {
  let temp: { ft: number, in: number, label: string }[] = [];
  heightFt.forEach((ft) => {
    heightIn.forEach((inch) => {
      temp.push({ft: ft, in: inch, label: `${ft}' ${inch}"`})
    })
  })
  return temp;
})();

const selectedWeightLbs = ref<string | null>(null),
  selectedHeightFt = ref<string | null>(null),
  selectedHeightIn = ref<string | null>(null);

onMounted(async () => {
  $q.loading.show();
  await getHeightWeight();
  $q.loading.hide();
});

async function getHeightWeight() {
  $q.loading.show();

  await getCanaryHeightWeight().then((response) => {
    const result: CanaryPatientHeightWeight = response?.data?.result;
    parseCanaryPatientHeightWeight(result);
  }).finally(() => {
    $q.loading.hide();
  });
}

function parseCanaryPatientHeightWeight(weightHeight: CanaryPatientHeightWeight) {
  // Weight
  const weightGr = weightHeight?.weight ?? 0;
  selectedWeightLbs.value = weightGr > 0 ? Math.floor(weightGr / 453.5924).toString() : null;

  // Height
  const totalHeightMM = weightHeight?.height ?? 0;
  if (totalHeightMM > 0) {
    const totalHeightInches = Math.floor(totalHeightMM / 25.4); //convert from mm to inches
    selectedHeightFt.value = Math.floor(totalHeightInches / 12).toString();
    selectedHeightIn.value = (totalHeightInches % 12).toString();
  } else {
    selectedHeightFt.value = null;
    selectedHeightIn.value = null;
  }
}

function getCanaryPatientHeightWeight(): CanaryPatientHeightWeight {
  const weightLbs = !selectedWeightLbs.value ? 0 : +selectedWeightLbs.value;
  const heightFt = !selectedHeightFt.value ? 0 : +selectedHeightFt.value;
  const heightIn = !selectedHeightIn.value ? 0 : +selectedHeightIn.value;

  return {
    weight: weightLbs * 453.5924, //pounds to grams
    height: (heightFt * 12 + heightIn) * 25.4, //inches to millimeters
  };
}

async function onNextClick() {

  var heightWeight = getCanaryPatientHeightWeight();
  if (!heightWeight || !await localForm.value?.validate()) return;

  $q.loading.show();
  const response = await handleApiRequest(() => updateCanaryHeightWeight(heightWeight), null, $q).finally(() => {
    $q.loading.hide();
  });

  if (!response?.result) {
  } else {
    // todo this required manual intervention and redirection to the initial base station intro?
    var nextRoute = await getNextActivationRoute($q);
    router.push({name: nextRoute}).catch(() => {
    });
  }
}

async function onAbandonClick() {
  showAbandon.value = !showAbandon.value;
}

async function onInfoClick() {
  showContact.value = !showContact.value;
}

const formHeightVal = ref(null);

watch(formHeightVal, (nV) => {
  selectedHeightFt.value = nV.ft;
  selectedHeightIn.value = nV.in;
})

const formValid = computed(() => {
  return selectedHeightFt.value !== null && selectedHeightIn.value !== null && selectedWeightLbs.value !== null;
});

</script>
<template>
  <q-page padding class="canary setup height-weight">
    <q-form @submit="onNextClick" ref="localForm">
      <MultiViewLayout :onBackClick="onAbandonClick" back-icon-list="fa-solid fa-house-circle-xmark" >
        <template v-slot:title>
          {{ t('hbs_flow_title') }}
        </template>

        <!-- Title -->
        <template #heading>
          {{ t('piq_height') }} & {{ t('piq_weight') }}
        </template>

        <template v-slot:auxiliary>
          <zbm-btn-icon @click="onInfoClick()" icon="fa-regular fa-question-circle" size="24px" text-color="dark-teal"/>
        </template>
        <!-- View Unique Content -->
        <template #default>
          <div class="row zb-form">
            <!-- Height -->
            <div class="col-12">
              <div class="row">
                <!-- Label -->
                <div class="col-6">
                  <h4>{{t('piq_height')}}</h4>
                  <q-select v-model="formHeightVal"
                            :options="heightsOptions"
                            lazy-rules
                            :rules="[$zb.validators.required()]"
                            dropdown-icon="fa-solid fa-chevron-down"/>
                </div>
              </div>

            </div>
            <!-- Weight -->
            <div class="col-12">
              <div class="row">
                <!-- Label -->
                <div class="col-6">
                  <h4>{{ t('piq_weight') }}</h4>
                  <q-input v-model="selectedWeightLbs"
                           :rules="[$zb.validators.required(), $zb.validators.betweenVal(1, 2000)]"
                           lazy-rules
                           type="number"/>
                </div>
              </div>

            </div>

            <div class="col q-pt-lg self-end">
              <zbm-btn :color="$zb.enums.ButtonStdColorEnum.accent" :label="t('acc_gen_continue')" :outline="true"
                       :width="$zb.enums.ButtonWidthEnum.wFull" :height="$zb.enums.ButtonHeightEnum.lg"
                       :disabled="!formValid" @click="onNextClick"/>
            </div>
          </div>

        </template>

      </MultiViewLayout>
      <the-abandon-modal v-model="showAbandon"/>

      <the-contact-modal v-model="showContact"/>
    </q-form>
  </q-page>
</template>

